import type {
	EntryMetaDateFormat,
	EntryMetaElement,
	EntryMetaReviewerBadge,
} from "../types";

/** Join-row / resolved person used to pick primary author vs reviewer. */
export type EntryMetaAuthorLink = {
	imageUrl?: string | null;
	isPrimary?: boolean;
	name: string;
	profileUrl?: string | null;
	role?: string | null;
	title?: string | null;
};

/** One taxonomy term on the entry; `href` = term archive path (null = unlinked). */
export type EntryMetaTermChip = {
	href: string | null;
	name: string;
	slug: string;
};

/** Resolved entry fields for canvas preview and public render (host supplies at publish). */
export type EntryMetaResolved = {
	authorImageUrl: string | null;
	authorName: string;
	/**
	 * Public author href for the byline link: archive (`/author/{slug}`) when
	 * enabled, else profile entry route, else external `profile_url`.
	 */
	authorProfileUrl: string | null;
	/** Job title / credentials, e.g. "MD, Internal Medicine". */
	authorTitle: string | null;
	publishedAt: string;
	readTimeMinutes: number;
	reviewerImageUrl: string | null;
	reviewerName: string | null;
	reviewerProfileUrl: string | null;
	reviewerTitle: string | null;
	/** Taxonomy terms on the entry (shown when `showTaxonomies` is on). */
	terms?: EntryMetaTermChip[];
	updatedAt: string;
};

export const ENTRY_META_DEFAULT_AUTHOR_LABEL = "Written by";
export const ENTRY_META_DEFAULT_REVIEWER_LABEL = "Reviewed by";
export const ENTRY_META_DEFAULT_PUBLISHED_PREFIX = "Published";
export const ENTRY_META_DEFAULT_UPDATED_PREFIX = "Updated";

export const ENTRY_META_REVIEWER_BADGE_LABELS = {
	fact_checked: "Fact-checked by",
	legally_reviewed: "Legally reviewed by",
	medically_reviewed: "Medically reviewed by",
	none: ENTRY_META_DEFAULT_REVIEWER_LABEL,
} as const satisfies Record<EntryMetaReviewerBadge, string>;

const REVIEWER_ROLES = new Set([
	"reviewer",
	"medical_reviewer",
	"legal_reviewer",
	"fact_checker",
]);

/** Sample data for editor canvas when entry context is unavailable. */
export const ENTRY_META_EDITOR_PLACEHOLDER: EntryMetaResolved = {
	authorImageUrl: null,
	authorName: "Alex Morgan",
	authorProfileUrl: null,
	authorTitle: "Senior Editor",
	publishedAt: "2026-05-25T12:00:00.000Z",
	readTimeMinutes: 23,
	reviewerImageUrl: null,
	reviewerName: "Dr. Priya Shah",
	reviewerProfileUrl: null,
	reviewerTitle: "MD, Internal Medicine",
	terms: [
		{ name: "Guides", slug: "guides", href: null },
		{ name: "Tax", slug: "tax", href: null },
	],
	updatedAt: "2026-05-25T14:30:00.000Z",
};

const DATE_FORMAT_OPTIONS: Record<
	EntryMetaDateFormat,
	Intl.DateTimeFormatOptions
> = {
	short: { month: "numeric", day: "numeric", year: "2-digit" },
	medium: { month: "short", day: "numeric", year: "numeric" },
	long: { month: "long", day: "numeric", year: "numeric" },
};

function trimOrEmpty(value: string | null | undefined): string {
	return value?.trim() ?? "";
}

function trimOrNull(value: string | null | undefined): string | null {
	const trimmed = value?.trim() ?? "";
	return trimmed.length > 0 ? trimmed : null;
}

/** Format an ISO timestamp for display in the metadata bar. */
export function formatEntryMetaDate(
	iso: string | null | undefined,
	format: EntryMetaDateFormat = "medium",
	locale?: string
): string {
	if (!iso?.trim()) {
		return "";
	}
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) {
		return "";
	}
	return date.toLocaleDateString(locale, DATE_FORMAT_OPTIONS[format]);
}

/**
 * Date with an optional prefix. Empty/whitespace prefix returns just the date
 * (no leftover space).
 */
export function formatEntryMetaDatedLabel(
	iso: string | null | undefined,
	format: EntryMetaDateFormat = "medium",
	prefix = ""
): string {
	const dateLabel = formatEntryMetaDate(iso, format);
	if (!dateLabel) {
		return "";
	}
	const trimmedPrefix = prefix.trim();
	return trimmedPrefix.length > 0 ? `${trimmedPrefix} ${dateLabel}` : dateLabel;
}

/** e.g. `23 min read` */
export function formatReadTime(minutes: number, suffix = "min read"): string {
	const rounded = Math.max(1, Math.round(minutes));
	const trimmedSuffix = suffix.trim();
	return trimmedSuffix.length > 0
		? `${rounded} ${trimmedSuffix}`
		: String(rounded);
}

const WORD_SPLIT = /\s+/u;

/** Estimate read time from body text (~200 words per minute by default). */
export function computeReadTimeMinutes(
	bodyText: string,
	wordsPerMinute = 200
): number {
	const wpm = wordsPerMinute > 0 ? wordsPerMinute : 200;
	const words = bodyText.trim().split(WORD_SPLIT).filter(Boolean).length;
	if (words === 0) {
		return 1;
	}
	return Math.max(1, Math.ceil(words / wpm));
}

function toIsoTimestamp(value: Date | string | null | undefined): string {
	if (value == null) {
		return "";
	}
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? "" : value.toISOString();
	}
	const trimmed = value.trim();
	if (!trimmed) {
		return "";
	}
	const parsed = new Date(trimmed);
	return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString();
}

export function isEntryMetaReviewerRole(
	role: string | null | undefined
): boolean {
	return REVIEWER_ROLES.has(trimOrEmpty(role).toLowerCase());
}

/**
 * Primary byline person vs reviewer. Explicit reviewer roles win; otherwise the
 * first non-primary link is the reviewer. A single person never becomes reviewer.
 */
export function splitEntryMetaPeople(links: EntryMetaAuthorLink[]): {
	author: EntryMetaAuthorLink | null;
	reviewer: EntryMetaAuthorLink | null;
} {
	const active = links.filter((link) => trimOrEmpty(link.name).length > 0);
	if (active.length === 0) {
		return { author: null, reviewer: null };
	}
	const author = active.find((link) => link.isPrimary) ?? active[0] ?? null;
	if (!author) {
		return { author: null, reviewer: null };
	}
	const byRole = active.find(
		(link) => link !== author && isEntryMetaReviewerRole(link.role)
	);
	const reviewer = byRole ?? active.find((link) => link !== author) ?? null;
	return { author, reviewer };
}

/**
 * Free-text prefix. `undefined` uses the default (legacy pages); explicit `""`
 * means no prefix — never a leftover colon or space.
 */
export function resolveEntryMetaPrefix(
	stored: string | undefined,
	fallback: string
): string {
	if (stored === undefined) {
		return fallback;
	}
	return stored.trim();
}

export function resolveEntryMetaAuthorLabel(
	stored: string | undefined
): string {
	return resolveEntryMetaPrefix(stored, ENTRY_META_DEFAULT_AUTHOR_LABEL);
}

export function resolveEntryMetaReviewerLabel(
	stored: string | undefined,
	badge: EntryMetaReviewerBadge | undefined
): string {
	if (stored !== undefined) {
		return stored.trim();
	}
	if (badge && badge !== "none") {
		return ENTRY_META_REVIEWER_BADGE_LABELS[badge];
	}
	return ENTRY_META_DEFAULT_REVIEWER_LABEL;
}

/** Legacy pages omit the prefix; new elements persist `"Published"`. */
export function resolveEntryMetaPublishedPrefix(
	stored: string | undefined
): string {
	return stored?.trim() ?? "";
}

/** Name plus optional credentials: `Jane Doe, MD`. */
export function formatEntryMetaPersonName(
	name: string,
	title: string | null | undefined,
	showTitle: boolean
): string {
	const trimmedName = trimOrEmpty(name);
	if (!trimmedName) {
		return "";
	}
	const creds = showTitle ? trimOrEmpty(title) : "";
	return creds.length > 0 ? `${trimmedName}, ${creds}` : trimmedName;
}

export function profileHrefWhenLinked(
	url: string | null | undefined,
	linkEnabled: boolean
): string {
	if (!linkEnabled) {
		return "";
	}
	return trimOrEmpty(url);
}

/** Build resolved metadata from entry row fields (publish + editor preview). */
export function buildEntryMetaResolved(input: {
	authorId?: string | null;
	authorImageUrl?: string | null;
	authorName?: string | null;
	authorProfileUrl?: string | null;
	authorTitle?: string | null;
	authors?: EntryMetaAuthorLink[];
	bodyText?: string | null;
	publishedAt?: Date | string | null;
	reviewerImageUrl?: string | null;
	reviewerName?: string | null;
	reviewerProfileUrl?: string | null;
	reviewerTitle?: string | null;
	terms?: EntryMetaTermChip[];
	updatedAt?: Date | string | null;
}): EntryMetaResolved {
	const publishedAt = toIsoTimestamp(input.publishedAt);
	const updatedAt = toIsoTimestamp(input.updatedAt) || publishedAt;
	const fromLinks =
		input.authors && input.authors.length > 0
			? splitEntryMetaPeople(input.authors)
			: null;

	let authorName = trimOrEmpty(fromLinks?.author?.name ?? input.authorName);
	if (!authorName && input.authorId?.trim()) {
		authorName = "Author";
	}

	const reviewerName = trimOrNull(
		fromLinks?.reviewer?.name ?? input.reviewerName
	);

	return {
		authorName,
		authorImageUrl: trimOrNull(
			fromLinks?.author?.imageUrl ?? input.authorImageUrl
		),
		authorProfileUrl: trimOrNull(
			fromLinks?.author?.profileUrl ?? input.authorProfileUrl
		),
		authorTitle: trimOrNull(fromLinks?.author?.title ?? input.authorTitle),
		reviewerName,
		reviewerImageUrl: reviewerName
			? trimOrNull(fromLinks?.reviewer?.imageUrl ?? input.reviewerImageUrl)
			: null,
		reviewerProfileUrl: reviewerName
			? trimOrNull(fromLinks?.reviewer?.profileUrl ?? input.reviewerProfileUrl)
			: null,
		reviewerTitle: reviewerName
			? trimOrNull(fromLinks?.reviewer?.title ?? input.reviewerTitle)
			: null,
		publishedAt,
		updatedAt,
		readTimeMinutes: computeReadTimeMinutes(input.bodyText ?? ""),
		terms: (input.terms ?? []).flatMap((term) => {
			const name = trimOrEmpty(term.name);
			const slug = trimOrEmpty(term.slug);
			return name ? [{ name, slug, href: trimOrNull(term.href) }] : [];
		}),
	};
}

/** Whether the updated date row should appear (optional hide when same calendar day as published). */
export function shouldShowUpdatedDate(
	published: string | null | undefined,
	updated: string | null | undefined,
	hideWhenSame: boolean
): boolean {
	if (!updated?.trim()) {
		return false;
	}
	if (!hideWhenSame) {
		return true;
	}
	if (!published?.trim()) {
		return true;
	}
	const publishedMs = new Date(published).getTime();
	const updatedMs = new Date(updated).getTime();
	if (Number.isNaN(publishedMs) || Number.isNaN(updatedMs)) {
		return true;
	}
	return publishedMs !== updatedMs;
}

function optionalResolvedString(value: unknown): string | null {
	return typeof value === "string" ? trimOrNull(value) : null;
}

function parseResolvedTermChips(raw: unknown): EntryMetaTermChip[] {
	if (!Array.isArray(raw)) {
		return [];
	}
	return raw.flatMap((item) => {
		if (!item || typeof item !== "object" || Array.isArray(item)) {
			return [];
		}
		const o = item as Record<string, unknown>;
		const name = typeof o.name === "string" ? o.name.trim() : "";
		if (!name) {
			return [];
		}
		return [
			{
				name,
				slug: typeof o.slug === "string" ? o.slug.trim() : "",
				href: optionalResolvedString(o.href),
			},
		];
	});
}

/** Parse injected `__entryMetaResolvedJson` without trusting the blob. */
export function parseEntryMetaResolvedJson(
	raw: unknown
): EntryMetaResolved | null {
	if (typeof raw !== "string" || !raw.trim()) {
		return null;
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
			return null;
		}
		const o = parsed as Record<string, unknown>;
		const authorName = typeof o.authorName === "string" ? o.authorName : "";
		const publishedAt = typeof o.publishedAt === "string" ? o.publishedAt : "";
		const updatedAt = typeof o.updatedAt === "string" ? o.updatedAt : "";
		const readTimeMinutes =
			typeof o.readTimeMinutes === "number" &&
			Number.isFinite(o.readTimeMinutes)
				? o.readTimeMinutes
				: 0;
		return {
			authorName,
			authorImageUrl: optionalResolvedString(o.authorImageUrl),
			authorProfileUrl: optionalResolvedString(o.authorProfileUrl),
			authorTitle: optionalResolvedString(o.authorTitle),
			reviewerName: optionalResolvedString(o.reviewerName),
			reviewerImageUrl: optionalResolvedString(o.reviewerImageUrl),
			reviewerProfileUrl: optionalResolvedString(o.reviewerProfileUrl),
			reviewerTitle: optionalResolvedString(o.reviewerTitle),
			publishedAt,
			updatedAt,
			readTimeMinutes,
			terms: parseResolvedTermChips(o.terms),
		};
	} catch {
		return null;
	}
}

export type EntryMetaDisplayConfig = Pick<
	EntryMetaElement,
	| "authorLabel"
	| "avatarSize"
	| "dateFormat"
	| "hideUpdatedWhenSameAsPublished"
	| "layoutDirection"
	| "linkAuthorProfile"
	| "linkReviewerProfile"
	| "linkTaxonomyTerms"
	| "publishedDatePrefix"
	| "readTimeSuffix"
	| "reviewerBadge"
	| "reviewerLabel"
	| "separatorStyle"
	| "showAuthor"
	| "showAuthorAvatar"
	| "showAuthorTitle"
	| "showPublishedDate"
	| "showPublishedIcon"
	| "showReadTime"
	| "showReadTimeIcon"
	| "showReviewer"
	| "showReviewerAvatar"
	| "showReviewerTitle"
	| "showSeparators"
	| "showTaxonomies"
	| "showUpdatedDate"
	| "showUpdatedIcon"
	| "updatedDatePrefix"
>;

export type EntryMetaPersonSegment = {
	href: string;
	imageUrl: string | null;
	key: "author" | "reviewer";
	label: string;
	name: string;
	showAvatar: boolean;
};

export type EntryMetaTextSegment = {
	key: "read" | "published" | "updated";
	text: string;
};

/** Taxonomy term chip ready for display; `href` is `""` when unlinked. */
export type EntryMetaTermSegment = {
	href: string;
	key: string;
	name: string;
};

export type EntryMetaBuiltSegments = {
	people: EntryMetaPersonSegment[];
	terms: EntryMetaTermSegment[];
	texts: EntryMetaTextSegment[];
};

/** Shared visibility + copy for canvas and public runtime. */
export function buildEntryMetaDisplaySegments(
	config: EntryMetaDisplayConfig,
	resolved: EntryMetaResolved
): EntryMetaBuiltSegments {
	const people: EntryMetaPersonSegment[] = [];
	const texts: EntryMetaTextSegment[] = [];

	if (config.showAuthor !== false && resolved.authorName.trim()) {
		people.push({
			key: "author",
			label: resolveEntryMetaAuthorLabel(config.authorLabel),
			name: formatEntryMetaPersonName(
				resolved.authorName,
				resolved.authorTitle,
				config.showAuthorTitle !== false
			),
			href: profileHrefWhenLinked(
				resolved.authorProfileUrl,
				config.linkAuthorProfile !== false
			),
			imageUrl: trimOrNull(resolved.authorImageUrl),
			showAvatar: config.showAuthorAvatar !== false,
		});
	}

	const reviewerName = resolved.reviewerName?.trim() ?? "";
	if (config.showReviewer !== false && reviewerName) {
		people.push({
			key: "reviewer",
			label: resolveEntryMetaReviewerLabel(
				config.reviewerLabel,
				config.reviewerBadge
			),
			name: formatEntryMetaPersonName(
				reviewerName,
				resolved.reviewerTitle,
				config.showReviewerTitle !== false
			),
			href: profileHrefWhenLinked(
				resolved.reviewerProfileUrl,
				config.linkReviewerProfile !== false
			),
			imageUrl: trimOrNull(resolved.reviewerImageUrl),
			showAvatar: config.showReviewerAvatar !== false,
		});
	}

	if (config.showReadTime !== false) {
		const readText = formatReadTime(
			resolved.readTimeMinutes,
			config.readTimeSuffix ?? "min read"
		);
		if (readText) {
			texts.push({ key: "read", text: readText });
		}
	}

	const dateFormat = config.dateFormat ?? "medium";
	if (config.showPublishedDate !== false) {
		const publishedText = formatEntryMetaDatedLabel(
			resolved.publishedAt,
			dateFormat,
			resolveEntryMetaPublishedPrefix(config.publishedDatePrefix)
		);
		if (publishedText) {
			texts.push({ key: "published", text: publishedText });
		}
	}

	if (
		config.showUpdatedDate !== false &&
		shouldShowUpdatedDate(
			resolved.publishedAt,
			resolved.updatedAt,
			config.hideUpdatedWhenSameAsPublished !== false
		)
	) {
		const updatedText = formatEntryMetaDatedLabel(
			resolved.updatedAt,
			dateFormat,
			resolveEntryMetaPrefix(
				config.updatedDatePrefix,
				ENTRY_META_DEFAULT_UPDATED_PREFIX
			)
		);
		if (updatedText) {
			texts.push({ key: "updated", text: updatedText });
		}
	}

	// Opt-in — existing pages keep their current meta bar untouched.
	const terms: EntryMetaTermSegment[] =
		config.showTaxonomies === true
			? (resolved.terms ?? []).map((term, index) => ({
					key: `term-${term.slug || term.name}-${index}`,
					name: term.name,
					href:
						config.linkTaxonomyTerms === false ? "" : (term.href ?? "").trim(),
				}))
			: [];

	return { people, terms, texts };
}
